import * as React from 'react';
import { Formik } from 'formik';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation, Trans } from 'react-i18next';
import { Typography, Button, AppBar, Toolbar, Grid } from '@material-ui/core';
import { Input, Phone, Password, Checkbox } from '../Shared/BsInput';
import { RegisterSchema } from '../../validation-schemas';
import { getAppConfig } from '../../config';

const REACT_TOS_URL = process.env.REACT_APP_TOS_URL || '';
const REACT_PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || '';

const RenderForm = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  isSubmitting,
  setFieldValue,
  isValid,
  t,
}) => {
  const VARIANT = (process.env.REACT_APP_VARIANT || '').toLowerCase();
  const brandName = VARIANT === 'urclyrd' ? 'Urclyrd' : 'Podesubir';
  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <Input
        type="email"
        value={values.email}
        onChange={handleChange}
        name="email"
        label={t('Email')}
        errors={errors}
        //disabled={blockMail}
      />
      <Phone
        onChange={(v) => {
          setFieldValue('cellphone', v);
        }}
        label={t('Cellphone number')}
        errors={errors}
        name="cellphone"
      />
      <Password
        value={values.password}
        onChange={handleChange}
        name="password"
        label={t('Password')}
        errors={errors}
      />
      <Password
        value={values.password_confirm}
        onChange={handleChange}
        name="password_confirm"
        label={t('Confirm password')}
        errors={errors}
      />
      <hr />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox name="tos" value={values.tos} onChange={handleChange} errors={errors} />
        <>
          {REACT_TOS_URL ? (
            <a href={REACT_TOS_URL} target="_blank" rel="noopener noreferrer">
              {t('Terms and Conditions')}
            </a>
          ) : (
            <span>{t('Terms and Conditions')}</span>
          )}
        </>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox name="privacy" value={values.privacy} onChange={handleChange} errors={errors} />
        <>
          {REACT_PRIVACY_URL ? (
            <a href={REACT_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              {t('Privacy policy')}
            </a>
          ) : (
            <span>{t('Privacy policy')}</span>
          )}
        </>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <Checkbox
          name="sms_consent"
          value={values.sms_consent}
          onChange={handleChange}
          errors={errors}
        />
        <div style={{ flex: 1 }}>
          <Typography variant="body2" component="div" style={{ lineHeight: 1.4, fontSize: '1rem' }}>
            {t('SMS consent', { brand: brandName })}
          </Typography>
          <div
            style={{
              marginTop: 8,
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'rgba(0,0,0,0.04)',
            }}
          >
            <Typography
              variant="caption"
              color="textSecondary"
              component="div"
              style={{ lineHeight: 1.4 }}
            >
              <Trans
                i18nKey="SMS consent modal body"
                values={{
                  brand: brandName,
                  helpEmail: (getAppConfig()?.links.contact || '').replace('mailto:', ''),
                }}
                components={[
                  <a
                    href={getAppConfig()?.links.terms}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ font: 'inherit', color: 'inherit' }}
                  />,
                  <a
                    href={getAppConfig()?.links.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ font: 'inherit', color: 'inherit' }}
                  />,
                  <a
                    href={getAppConfig()?.links.contact}
                    style={{ font: 'inherit', color: 'inherit' }}
                  />,
                ]}
              />
            </Typography>
          </div>
        </div>
      </div>
      <Button
        className="mt-2"
        variant="outlined"
        color="default"
        type="submit"
        disabled={isSubmitting || !isValid || !values.sms_consent}
        fullWidth
      >
        {t('Sign up')}
      </Button>
    </form>
  );
};

const useStyles = makeStyles((theme) => ({
  appBar: {
    top: 'auto',
    bottom: 0,
  },
  toolBar: {
    minHeight: '50px',
  },
  link: {
    color: theme.palette.secondary.main,
    textDecoration: 'underline',

    '&:hover': {
      color: theme.palette.secondary.main,
      textDecoration: 'none',
    },
  },
}));

export default ({ onSubmit, mail }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const decodeMail = () => {
    return Buffer.from(mail, 'base64').toString('binary');
  };

  return (
    <React.Fragment>
      <Grid container direction="row" justify="center" spacing={3} style={{ marginBottom: '20%' }}>
        <Grid item xs={12} sm={8} md={4}>
          <Typography align="center" variant="h6">
            {t('Register')}
          </Typography>
          <Formik
            initialValues={{
              email: mail ? decodeMail() : '',
              cellphone: '',
              password: '',
              password_confirm: '',
              tos: false,
              privacy: false,
              sms_consent: false,
            }}
            validationSchema={RegisterSchema}
            enableReinitialize={true}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
              try {
                const { sms_consent, ...rest } = values;
                const clean = {
                  ...rest,
                  cellphone: rest.cellphone.replace('+', ''),
                };

                await onSubmit(clean);
              } catch (err) {
                if (err.response && err.response.data.errors) {
                  setErrors(err.response.data.errors);
                }

                setSubmitting(false);
              }
            }}
            render={(p) => (
              <RenderForm {...p} {...{ t }} blockMail={!!mail} linkClassName={classes.link} />
            )}
          />
        </Grid>
      </Grid>
      <AppBar position="fixed" color="default" className={classes.appBar}>
        <Toolbar className={classes.toolBar}>
          <Typography align="center" variant="body2" component="div" style={{ flex: 1 }}>
            {t('Already have an account?')}{' '}
            <Link className={classes.link} to="/login">
              {t('sign in')}
            </Link>
          </Typography>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};
